import React, { useCallback, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { observer } from 'mobx-react';
import cm from 'classnames';
import { Button } from 'ksnm-common-ui/lib/Button';
import { Icon } from 'ksnm-common-ui/lib/Icon';
import { Tree } from 'ksnm-common-ui/lib/Tree';

import { DesignLayout } from '@common/DesignLayout';
import { RoutePath } from '@components/AppRouter/constants';
import { useCheckFromDictionaryCard } from '@src/utils/hooks/useCheckFromDictionaryCard';
import { usePermission } from '@src/utils/hooks/usePermission';
import { recursiveFindByCode } from '@src/utils/tree';
import { fetchReferences } from '@stores/dictionaryStores/fetchReferences';
import { reportPeriodsStore } from '@stores/dictionaryStores/reportPeriodsStore';
import { reportVersionsStore } from '@stores/dictionaryStores/reportVersion/reportVersionsStore';

import { NEW_ENTITY_ID } from '../../../AppRouter/hooks/constants';
import { useAuditView } from '../../hooks/useAuditView';
import { Filter } from './Filter';
import { Table } from './Table';
import { toTreeView } from './toTreeView';

import styles from './ReportVersions.module.scss';

const ReportVersionsComponent = () => {
  const {
    fetch,
    reportVersions,
    filters,
    clear,
    selectedKeys,
    expandedKeys,
    selectNode,
    fullSize,
    setSelectNode,
    setBreadcrumbs,
    setExpandedKeys,
    setSelectedKeys,
  } = reportVersionsStore;
  const { push } = useHistory();
  const { groupInPeriods } = reportPeriodsStore;

  const permission = usePermission('CmReportVersion');

  const isBack = useCheckFromDictionaryCard(RoutePath.DICTIONARY_REPORT_VERSION);

  useAuditView('Версии отчетов');

  useEffect(() => {
    fetchReferences(['reportPeriods']);
    fetch();
  }, [fetch]);

  useEffect(() => {
    if (!isBack) clear();
  }, [clear, isBack]);

  const treeData = useMemo(() => {
    // отображаем только те группы, по которым есть настройка в Календаре отчетных форм
    const data = reportVersions.filter((item) => groupInPeriods.includes(item.reportGroupCode));

    return toTreeView(
      data.filter(
        (report) =>
          !(
            (filters.reportGroup && report.reportGroupCode !== filters.reportGroup) ||
            (filters.reportType && report.reportTypeCode !== filters.reportType)
          ),
      ),
    );
  }, [filters.reportGroup, filters.reportType, groupInPeriods, reportVersions]);

  const onSelect = useCallback(
    (_, { node, selected }) => {
      if (selected) {
        setSelectNode(node);
        setSelectedKeys([node.key]);

        const nodes = recursiveFindByCode(node.key, treeData);
        if (nodes) setBreadcrumbs(nodes.map((item) => item.title));
      }

      if (!selected) setSelectedKeys([]);
    },
    [setBreadcrumbs, setSelectNode, setSelectedKeys, treeData],
  );

  const onExpand = useCallback(
    (keys) => {
      setExpandedKeys(keys);
    },
    [setExpandedKeys],
  );

  const onCreate = useCallback(() => {
    push(`${RoutePath.DICTIONARY_REPORT_VERSION}/${NEW_ENTITY_ID}`);
  }, [push]);

  const actions = useMemo(
    () =>
      !selectNode && permission.CREATE ? (
        <Button onClick={onCreate} tooltip="Добавить">
          <Icon name="add" />
        </Button>
      ) : null,
    [onCreate, permission, selectNode],
  );

  return (
    <DesignLayout scrollEnable>
      <Filter />
      <div className={styles.container}>
        <Tree
          selectable
          treeData={treeData}
          onSelect={onSelect}
          className={cm(styles.tree, {
            [styles.withTable]: selectNode,
            [styles.fullSize]: fullSize,
          })}
          selectedKeys={selectedKeys}
          expandedKeys={expandedKeys}
          onExpand={onExpand}
          actions={actions}
        />
        <Table />
      </div>
    </DesignLayout>
  );
};

export const ReportVersions = observer(ReportVersionsComponent);
